import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { youtubeThumbUrl } from '../data/homePortfolio';
import './MarqueeRows.css';

const PlayGlyph = memo(() => (
  <span className="marquee-play" aria-hidden="true">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5.14v13.72L19 12 8 5.14z" />
    </svg>
  </span>
));

PlayGlyph.displayName = 'PlayGlyph';

/** Plain tiles - thumbs only; no hover transforms */
const MarqueeTile = memo(({ variant, src }) => (
  <div className={`marquee-tile marquee-tile--${variant}`}>
    <img
      src={src}
      alt=""
      loading="eager"
      decoding="async"
      width={variant === 'video' ? 320 : 480}
      height={variant === 'video' ? 180 : 320}
      draggable={false}
    />
    {variant === 'video' ? <PlayGlyph /> : null}
  </div>
));

MarqueeTile.displayName = 'MarqueeTile';

const MarqueeRow = memo(({ items, rowIndex, variant, linkTo, direction, duration, active }) => {
  const [hovered, setHovered] = useState(false);
  const onEnter = useCallback(() => setHovered(true), []);
  const onLeave = useCallback(() => setHovered(false), []);

  const paused = !active || hovered;

  const renderTiles = (suffix) =>
    items.map((item, i) => {
      const isVideo = variant === 'video';
      return (
        <MarqueeTile
          key={`${suffix}-${rowIndex}-${item}-${i}`}
          variant={variant}
          src={isVideo ? youtubeThumbUrl(item) : item}
        />
      );
    });

  return (
    <div
      className={`marquee-row marquee-row--${direction}${paused ? ' is-paused' : ''}`}
      style={{ '--marquee-duration': `${duration}s` }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className="marquee-row-viewport">
        <div className="marquee-row-track">
          {renderTiles('a')}
          {renderTiles('b')}
        </div>
      </div>
      <Link
        className="marquee-row-hit"
        to={linkTo}
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
});

MarqueeRow.displayName = 'MarqueeRow';

/**
 * Multi-row infinite marquee.
 * Even rows LTR; odd rows RTL. Pauses when off-screen or row hovered.
 */
const MarqueeRows = ({ rows = [], variant = 'photo', linkTo = '/' }) => {
  const rootRef = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      setActive(false);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setActive(entry.isIntersecting && entry.intersectionRatio > 0.12);
      },
      { root: null, threshold: [0, 0.12, 0.25], rootMargin: '80px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!rows.length) return null;

  return (
    <div
      ref={rootRef}
      className={`marquee-rows marquee-rows--${variant}${active ? ' is-active' : ''}`}
      aria-hidden="true"
    >
      {rows.map((items, rowIndex) => (
        <MarqueeRow
          key={`row-${rowIndex}`}
          items={items}
          rowIndex={rowIndex}
          variant={variant}
          linkTo={linkTo}
          direction={rowIndex % 2 === 0 ? 'ltr' : 'rtl'}
          duration={42 + rowIndex * 6}
          active={active}
        />
      ))}
    </div>
  );
};

export default MarqueeRows;
